import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import { Program } from "@/domain/programs.interface";
import { Organization } from "@/domain/organizations.interface";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { SingleSelect } from "@/components/Select";
import { FormGroup, InputAdornment, TextField } from "@mui/material";
import { getEnrollmentStatusOptions } from "@/services/utils/enrollmentStatus.utils";

export type Filters = {
  sortField: string;
  sortOrder: string;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string[];
  program: string;
  orgUnitId: string;
  groupLabelId: string;
  notEnrolled: boolean;

  assignedPrograms: string[];
  assignedOrgUnits: string[];
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
  isLoadingButtons: boolean;
};

const ParticipantFilters: FC<Props> = ({ filtersState }) => {
  const [filters, setFilters] = filtersState;
  const { userInfo, userIsAdminOrSupport } = useUserStore();
  const isAdminOrSupport = userIsAdminOrSupport();
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [selectedOrganizationUnit, setSelectedOrganizationUnit] =
    useState<Organization | null>(null);
  const [assignedOrgUnits, setAssignedOrgUnits] = useState<string[]>([]); // empty for admin roles
  const isProgramFilterDisabled = !!filters.notEnrolled;
  const isOrgUnitFilterDisabled =
    !!filters.notEnrolled || filters.program === "all";
  const isGroupLabelFilterDisabled =
    !!filters.notEnrolled || filters.program === "all";
  const isStatusFilterDisabled = !!filters.notEnrolled;

  const { programs } = useGetUserPrograms({
    userId: userInfo?._id as string,
    sortType: "desc",
    hasSystemAdminRole: isAdminOrSupport,
  });

  const programsOptions = useMemo(() => {
    if (!programs) return [];
    const options =
      programs.length > 0 ? [{ key: "all", value: "All programs" }] : [];

    // Sort programs alphabetically by name per Field Spec
    const sortedPrograms = [...programs].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );

    sortedPrograms.forEach((program) => {
      if (program.name) {
        options.push({ key: program._id, value: program.name });
      }
    });

    return options;
  }, [programs]);

  useEffect(() => {
    if (programs && programs.length > 0) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        assignedPrograms: programs.map((it) => it._id),
      }));
    }
  }, [programs, setFilters]);

  useEffect(() => {
    if (!userInfo) return;
    if (!programs || programs.length === 0) return;
    let userOrgUnitIds: string[] = [];
    if (isAdminOrSupport) {
      userOrgUnitIds = programs
        .flatMap((it) => it.orgUnitIds)
        .filter((it): it is string => typeof it === "string" && !!it);
    } else {
      userOrgUnitIds = userInfo.assignedOrgUnits
        .filter((orgUnit) => programs.some((p) => p._id === orgUnit.programId))
        .flatMap((orgUnit) => orgUnit.orgUnitIds)
        .filter((it): it is string => typeof it === "string" && !!it);
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      assignedOrgUnits: userOrgUnitIds || [],
    }));
    setAssignedOrgUnits(userOrgUnitIds);
  }, [userInfo, isAdminOrSupport, programs]);

  const hasOrgUnits = useMemo(() => {
    return !!assignedOrgUnits && (assignedOrgUnits?.length ?? 0) > 0;
  }, [assignedOrgUnits]);

  const orgUnitIds = useMemo(() => {
    const programOrgUnitIds =
      hasOrgUnits && assignedOrgUnits ? assignedOrgUnits : [];
    return programOrgUnitIds.filter((it) =>
      filters.assignedOrgUnits.includes(it),
    );
  }, [hasOrgUnits, assignedOrgUnits, filters.assignedOrgUnits]);

  const { organizationUnits, refetchOrganizationUnits } =
    useOrganizationUnitsByIds({
      ids: orgUnitIds,
      status: "Active",
      enabled: hasOrgUnits,
    });

  const organizationUnitsOptions = useMemo(() => {
    if (!organizationUnits || !organizationUnits.organizations) return [];
    return organizationUnits.organizations
      .filter(
        (it) =>
          (selectedProgram?.orgUnitIds || []).includes(it._id) &&
          assignedOrgUnits.includes(it._id),
      )
      .map((orgUnit) => ({
        key: orgUnit._id,
        value: orgUnit.name,
      }));
  }, [organizationUnits, selectedProgram, assignedOrgUnits]);

  const organizationGroupLabelsOptions = useMemo(() => {
    if (!organizationUnits) return [];

    // Primero aplanar el array de groupLabels
    const allGroupLabels = organizationUnits.organizations
      .map((orgUnit) => orgUnit.groupLabels)
      .flat();

    // Usar un Map para eliminar duplicados basados en el ID
    const uniqueGroupLabelsMap = new Map();

    allGroupLabels.forEach((groupLabel) => {
      if (!uniqueGroupLabelsMap.has(groupLabel._id)) {
        uniqueGroupLabelsMap.set(groupLabel._id, {
          key: groupLabel._id,
          value: groupLabel.name,
        });
      }
    });

    // Convertir el Map de vuelta a un array
    return Array.from(uniqueGroupLabelsMap.values());
  }, [organizationUnits]);

  const statusOptions = useMemo(() => {
    const options: Array<{ key: string; value: string }> = [];

    const sorted = [...getEnrollmentStatusOptions()].sort((a, b) =>
      (a || "").localeCompare(b || ""),
    );

    sorted.forEach((item) => {
      if (item) {
        options.push({ key: item, value: item });
      }
    });

    return options;
  }, []);

  useEffect(() => {
    if (selectedProgram) {
      refetchOrganizationUnits();
    }
  }, [selectedProgram, refetchOrganizationUnits]);

  // COYN-915 SC2: Sync selectedProgram/selectedOrgUnit from filters when returning from profile
  // Ensures Org unit dropdown options appear correctly after navigation
  useEffect(() => {
    if (!programs || programs.length === 0) return;
    if (filters.program && filters.program !== "all" && !selectedProgram) {
      const program = programs.find((p) => p._id === filters.program);
      if (program) setSelectedProgram(program);
    }
  }, [programs, filters.program, selectedProgram]);

  useEffect(() => {
    if (!organizationUnits?.organizations || !filters.orgUnitId) return;
    if (!selectedOrganizationUnit) {
      const orgUnit = organizationUnits.organizations.find(
        (ou) => ou._id === filters.orgUnitId,
      );
      if (orgUnit) setSelectedOrganizationUnit(orgUnit);
    }
  }, [
    organizationUnits?.organizations,
    filters.orgUnitId,
    selectedOrganizationUnit,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        <TextField
          placeholder="Search participant"
          value={filters.search}
          onChange={(e) => {
            setFilters((filters) => ({
              ...filters,
              search: e.target.value,
              pageNumber: 0,
            }));
          }}
          sx={{
            flex: { xs: "none", md: 1 },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "15.625rem" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "surface.main",
              "& fieldset": {
                borderColor: "surface.main",
              },
              "&:hover fieldset": {
                borderColor: "surface.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: "surface.main",
              },
            },
            height: "3.5rem",
          }}
          size="medium"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "icon.main" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.notEnrolled}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    notEnrolled: value.target.checked,
                    pageNumber: 0,
                  }));
                }}
              />
            }
            label="Not enrolled"
            componentsProps={{
              typography: {
                variant: "bodyLarge",
              },
            }}
            sx={{
              ".MuiFormControlLabel-label": {
                marginLeft: "0",
              },
            }}
          />
        </FormGroup>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 2,
          flexDirection: { xs: "column", md: "row" },
          justifyContent: { xs: "flex-start", md: "space-between" },
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        <Select
          id="program-filter"
          label="Program"
          value={filters.program}
          disabled={isProgramFilterDisabled}
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              program: value as string,
              orgUnitId: "",
              groupLabelId: "",
              pageNumber: 0,
            }));

            if (value) {
              setSelectedProgram(
                programs?.find((program) => program._id === value) as Program,
              );

              setSelectedOrganizationUnit(null);
            } else {
              setSelectedProgram(null);
            }
          }}
          data={programsOptions || []}
          sx={{ flex: 1 }}
        />
        <SingleSelect
          id="org-unit-filter"
          label="Org unit(s)"
          value={filters.orgUnitId}
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              orgUnitId: value as string,
              groupLabelId: "",
              pageNumber: 0,
            }));

            if (value) {
              setSelectedOrganizationUnit(
                organizationUnits?.organizations?.find(
                  (orgUnit) => orgUnit._id === value,
                ) as Organization,
              );
            } else {
              setSelectedOrganizationUnit(null);
            }
          }}
          data={organizationUnitsOptions}
          disabled={isOrgUnitFilterDisabled}
          showPlaceholder={true}
          placeholderText="[Select]"
        />
        <SingleSelect
          id="org-unit-label-filter"
          label="Org unit label"
          value={filters.groupLabelId}
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              groupLabelId: value as string,
              orgUnitId: "",
              pageNumber: 0,
            }));
          }}
          data={organizationGroupLabelsOptions || []}
          disabled={isGroupLabelFilterDisabled}
          showPlaceholder={true}
          placeholderText="[Select]"
        />

        <Select
          id="status-filter"
          label="Status"
          value={filters.status}
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              status: value as string[],
              pageNumber: 0,
            }));
          }}
          data={statusOptions}
          disabled={isStatusFilterDisabled}
          multiple={true}
        />
      </Box>
    </>
  );
};

export default ParticipantFilters;
